import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { cn } from '../lib/cn.ts'
import { Spinner } from './Spinner.tsx'
import { Tooltip } from './Tooltip.tsx'

export type ButtonVariant = 'primary' | 'secondary' | 'quiet' | 'danger'
export type ButtonSize = 'sm' | 'md' | 'lg'

type Props = ButtonHTMLAttributes<HTMLButtonElement> & {
	/** Required: every interactive element in this app carries a stable id. */
	id: string
	variant?: ButtonVariant
	size?: ButtonSize
	loading?: boolean
	icon?: ReactNode
}

/** Shared by `Button` and link-styled-as-button (`buttonClass`), so a `<Link>` can look the same. */
const base =
	'relative inline-flex select-none items-center justify-center whitespace-nowrap rounded-btn font-[560] ' +
	'transition-[background-color,border-color,color,box-shadow,scale] duration-(--dur-instant) ease-out ' +
	'active:scale-[.97] motion-reduce:active:scale-100 ' +
	'disabled:pointer-events-none disabled:opacity-45 aria-disabled:pointer-events-none aria-disabled:opacity-45'

const variants: Record<ButtonVariant, string> = {
	primary: 'bg-teal-700 text-white hover:bg-teal-600 active:bg-teal-800',
	secondary:
		'bg-sheet text-ink shadow-[inset_0_0_0_1px_var(--color-line)] hover:shadow-[inset_0_0_0_1px_var(--color-line-strong)] hover:bg-teal-50',
	quiet: 'bg-transparent text-ink-2 hover:bg-teal-50 hover:text-ink',
	// Quiet until aimed at: the red belongs to the moment of intent, not to the resting page.
	danger: 'bg-transparent text-ink-2 hover:bg-error-soft hover:text-error focus-visible:text-error',
}

// `sm` is 36px to the eye and 44px to the finger: the ::after extends the hit area.
const sizes: Record<ButtonSize, string> = {
	sm: 'h-9 gap-1.5 px-3 text-[0.875rem] after:absolute after:-inset-1 after:content-[""]',
	md: 'h-11 gap-2 px-4 text-[0.9375rem]',
	lg: 'h-13 gap-2 px-6 text-body',
}

export const buttonClass = (variant: ButtonVariant = 'secondary', size: ButtonSize = 'md') =>
	cn(base, variants[variant], sizes[size])

export function Button({
	variant = 'secondary',
	size = 'md',
	loading = false,
	icon,
	children,
	className,
	disabled,
	type = 'button',
	...rest
}: Props) {
	return (
		<button
			type={type}
			disabled={disabled || loading}
			aria-busy={loading || undefined}
			className={cn(
				base,
				variants[variant],
				sizes[size],
				loading && 'disabled:opacity-100',
				className,
			)}
			{...rest}
		>
			{loading ? <Spinner className="size-[1.125rem]" /> : icon}
			{children}
		</button>
	)
}

/** Square icon-only button. `label` is mandatory: it is the accessible name and the tooltip. */
export function IconButton({
	label,
	icon,
	className,
	size = 'md',
	variant = 'quiet',
	...rest
}: Omit<Props, 'children' | 'icon'> & { label: string; icon: ReactNode }) {
	return (
		<Tooltip label={label}>
			<Button
				aria-label={label}
				variant={variant}
				size={size}
				className={cn(
					size === 'sm' ? 'size-9' : size === 'lg' ? 'size-13' : 'size-11',
					'px-0',
					className,
				)}
				icon={icon}
				{...rest}
			/>
		</Tooltip>
	)
}
